import DeleteIcon from '@assets/icons/delete.svg';
import React, { useEffect, useRef, useState } from 'react';
import {
  Animated,
  StyleProp,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { LARGE_LAYOUT_CONTENT_MAX_WIDTH, shadow, TYPOGRAPHY } from '@/styles';
import { Theme, useTheme } from '@/contexts/theme-context';

const CLOSE_BUTTON_HIT_SLOP = {
  bottom: 8,
  left: 8,
  right: 8,
  top: 8,
};
export const DEFAULT_AUTO_CLOSE_TIMEOUT = 5000;

export const TOAST_MARGIN = 12;

export enum Type {
  DEFAULT,
  MINIMAL,
  NEGATIVE,
  POSITIVE,
  PRIMARY,
  WARNING,
}

const getTypeBackgroundColor = (type: Type, theme: Theme) => {
  return {
    [Type.DEFAULT]: theme.colors.inactiveText,
    [Type.MINIMAL]: theme.colors.transparent,
    [Type.NEGATIVE]: theme.colors.negative,
    [Type.POSITIVE]: theme.colors.darkGreen,
    [Type.PRIMARY]: theme.colors.primary,
    [Type.WARNING]: theme.colors.warning,
  }[type];
};

export interface ToastOptions {
  // Closes the toast after a specified timeout. Set to 0 to disable
  autoCloseTimeout?: number;
  closeButtonStyle?: StyleProp<ViewStyle>;
  closeIconColor?: string;
  containerStyle?: StyleProp<ViewStyle>;
  content: React.ReactNode | ((onClose: () => void) => React.ReactNode);
  onClose?: () => void;
  showCloseButton?: boolean;
  type?: Type;
}

interface Props {
  onClose: () => void;
  options: ToastOptions;
}

export const Toast: React.FC<Props> = ({ onClose, options }) => {
  const theme = useTheme();

  const autoCloseTimeout =
    options.autoCloseTimeout ?? DEFAULT_AUTO_CLOSE_TIMEOUT;
  const showCloseButton = options.showCloseButton ?? true;
  const type = options.type ?? Type.DEFAULT;

  const [closing, setClosing] = useState(false);
  const opacityValue = useRef(new Animated.Value(1));

  useEffect(() => {
    if (autoCloseTimeout) {
      const timeout = setTimeout(() => setClosing(true), autoCloseTimeout);
      return () => clearTimeout(timeout);
    }
  }, [autoCloseTimeout]);

  const optionsOnClose = options.onClose;
  useEffect(() => {
    if (closing) {
      Animated.timing(opacityValue.current, {
        duration: 250,
        toValue: 0,
        useNativeDriver: true,
      }).start(() => {
        optionsOnClose?.();
        onClose();
      });
    }
  }, [closing, onClose, optionsOnClose]);

  return (
    <Animated.View
      style={[
        styles.base,
        {
          backgroundColor: getTypeBackgroundColor(type, theme),
          opacity: opacityValue.current,
        },
        type === Type.MINIMAL ? styles.baseMinimal : undefined,
        options.containerStyle,
      ]}
    >
      <View style={styles.content}>
        {typeof options.content === 'string' ? (
          <Text style={TYPOGRAPHY.toast}>{options.content}</Text>
        ) : typeof options.content === 'function' ? (
          options.content(onClose)
        ) : (
          options.content
        )}
      </View>
      {showCloseButton ? (
        <TouchableOpacity
          hitSlop={CLOSE_BUTTON_HIT_SLOP}
          onPress={() => setClosing(true)}
          style={[styles.closeButton, options.closeButtonStyle]}
          testID="close-toast-button"
        >
          <DeleteIcon
            fill={options.closeIconColor || theme.colors.white}
            height={12}
            width={12}
          />
        </TouchableOpacity>
      ) : null}
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  base: {
    ...shadow({
      elevation: 24,
      opacity: 0.12,
      radius: 24,
      x: 0,
      y: 12,
    }),
    alignItems: 'center',
    alignSelf: 'center',
    borderRadius: 8,
    flexDirection: 'row',
    marginTop: TOAST_MARGIN,
    maxWidth: LARGE_LAYOUT_CONTENT_MAX_WIDTH,
    paddingHorizontal: 18,
    paddingVertical: 16,
    width: '100%',
  },
  baseMinimal: {
    paddingHorizontal: 0,
    paddingVertical: 0,
  },
  closeButton: {
    marginLeft: 18,
  },
  content: {
    flex: 1,
  },
});
